/**
 * Hotel Checkout Slice
 *
 * Manages hotel checkout/payment state.
 * Separate from booking review as checkout is a distinct step.
 *
 * Auto-resets when navigating away from hotel-review template
 * to prevent stale checkout URLs from causing unwanted redirects.
 */
import { createSlice } from '@reduxjs/toolkit';
import type { HotelCheckoutData } from '../api/checkout/checkoutApi';
import { hotelCheckoutThunk } from '../thunks/checkout';

// ============================================
// State Types
// ============================================

export interface HotelCheckoutState {
  isLoading: boolean;
  error: string | null;
  checkoutData: HotelCheckoutData | null;
}

// ============================================
// Initial State
// ============================================

export const initialHotelCheckoutState: HotelCheckoutState = {
  isLoading: false,
  error: null,
  checkoutData: null,
};

// ============================================
// Slice
// ============================================

const checkoutSlice = createSlice({
  name: 'hotels/checkout',
  initialState: initialHotelCheckoutState,
  reducers: {
    resetHotelCheckoutState: () => initialHotelCheckoutState,
    clearCheckoutError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(hotelCheckoutThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(hotelCheckoutThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.checkoutData = action.payload.checkoutData;
      })
      .addCase(hotelCheckoutThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Failed to initiate checkout';
      })
      // Reset checkout state when template changes (navigating away from review)
      // This prevents stale checkout URLs from causing unwanted redirects
      .addMatcher(
        (action) => action.type === 'chat/template/setActiveTemplate',
        (state, action: { payload: { template: string | null } }) => {
          // Only reset if navigating away from hotel-review template
          if (action.payload.template !== 'hotels-review' && state.checkoutData !== null) {
            state.isLoading = false;
            state.error = null;
            state.checkoutData = null;
          }
        },
      );
  },
});

// ============================================
// Exports
// ============================================

export const { resetHotelCheckoutState, clearCheckoutError } = checkoutSlice.actions;
export const checkoutReducer = checkoutSlice.reducer;
